import { useContext, useCallback, useMemo } from "react";
import { TableContext } from "@swiggy-private/portal-dineout-context";
import { useRouter } from "next/compat/router";
import { QUERY_PARAMS, STORAGE_KEYS } from "../constants";
import { useIsV2InApp } from "./useIsV2InApp";
import { navigateToDigitalMenu } from "../utils/nativeBridgeHelper";

interface UseNavigateToDigitalMenuResult {
    navigateToMenu: () => boolean;
    isPnWebviewBack: boolean;
    userJourney: string;
}

/**
 * Reads `pnWebviewBack` from the URL query param or sessionStorage,
 * and returns a function that navigates to the V2 native digital menu
 * when pnWebviewBack is true.
 *
 * Also reads a parallel `userJourney` signal (URL → sessionStorage). Back-nav
 * fires when EITHER `pnWebviewBack` is set OR `userJourney === LIVE_NOTIFICATION`,
 * so a Live Notification session on any non-menu portal page (e.g. bill-summary)
 * routes back to the native digital menu. `userJourney` is the forward-looking
 * key intended to eventually replace `pnWebviewBack` for both Push and Live
 * Notifications.
 *
 * navigateToMenu returns true if navigation was handled, false otherwise.
 * isPnWebviewBack is exposed for V1 fallback scenarios (e.g. deeplink with no history).
 * userJourney is exposed (empty string when not present) so callers can attach it to deeplinks.
 */
export const useNavigateToDigitalMenu = (): UseNavigateToDigitalMenuResult => {
    const router = useRouter();
    const { restaurantId = "", tableId = "" } = useContext(TableContext);
    const { isV2InApp } = useIsV2InApp();

    const isPnWebviewBack = useMemo(() => {
        const fromQuery = router?.query?.[QUERY_PARAMS.PN_WEBVIEW_BACK] === "true";

        if (fromQuery) {
            try {
                sessionStorage.setItem(STORAGE_KEYS.PN_WEBVIEW_BACK, "true");
            } catch {
                // sessionStorage unavailable (SSR or private browsing)
            }
            return true;
        }

        try {
            return sessionStorage.getItem(STORAGE_KEYS.PN_WEBVIEW_BACK) === "true";
        } catch {
            return false;
        }
    }, [router?.query]);

    const userJourney = useMemo(() => {
        const raw = router?.query?.[QUERY_PARAMS.USER_JOURNEY];
        const fromQuery = typeof raw === "string" ? raw : "";

        if (fromQuery) {
            try {
                sessionStorage.setItem(STORAGE_KEYS.USER_JOURNEY, fromQuery);
            } catch {
                // sessionStorage unavailable (SSR or private browsing)
            }
            return fromQuery;
        }

        try {
            return sessionStorage.getItem(STORAGE_KEYS.USER_JOURNEY) ?? "";
        } catch {
            return "";
        }
    }, [router?.query]);

    // Fire native back-nav if either of the two parallel signals is set:
    //   - `pnWebviewBack`              — legacy Push Notification signal
    //   - `userJourney=LIVE_NOTIFICATION` — new Live Notification signal
    // Once native unifies on `userJourney`, the `pnWebviewBack` branch can be
    // removed without touching call sites.
    // navigateToMenu only handles the pnWebviewBack (Push Notification) signal.
    // Live Notification journeys must NOT go through this — callers already have a
    // closeWebview() fallback that fires when this returns false, which is the
    // correct behaviour for live notifications on V2.
    const navigateToMenu = useCallback((): boolean => {
        if (isV2InApp && isPnWebviewBack) {
            navigateToDigitalMenu(restaurantId, tableId);
            return true;
        }
        return false;
    }, [isV2InApp, isPnWebviewBack, restaurantId, tableId]);

    return { navigateToMenu, isPnWebviewBack, userJourney };
};
